/**
 * Générateur — Automatisme : connaître les mesures des angles de l'équerre
 * dont dispose l'élève (30° 60° 90° ou 45° 45° 90°).
 * Attendu ID : 1075 | Niveau : 5ème | Domaine : Angles
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Les angles de mon équerre       (saisie de l'angle marqué ; QCM du triplet)
 *   - Quelle équerre pour cet angle ? (QCM quelle équerre trace directement n° — la saisie
 *                                     « accoler / retrancher » est retirée : combiner n'est pas connaître, 2026-09-04)
 *   - Équerres : vrai ou faux ?       (V/F pool + deux lignes numériques)
 *
 * ── Plan de cartes validé (2026-09-04) ─────────────────────────────────────
 * Source : Automatisme n°37 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * P1 : équerre INTRODUITE au CM1 comme instrument (angle droit, perpendiculaires) ;
 * en 6e ses angles sont CONSTATABLES (rapporteur, somme 180°, triangle rectangle
 * et isocèle) sans être un objet d'étude ; en 5e les six mesures deviennent un
 * AUTOMATISME. Le BO cycle 3 ne nomme jamais ces angles.
 * P2 : sens installés — l'équerre « coin droit », le triangle rectangle et le
 * rectangle isocèle, la somme 180°. Sens nouveau : l'équerre comme RÉSERVE
 * d'angles connus pour tracer sans rapporteur, en accolant ou en retranchant
 * (75 = 45 + 30, 135 = 90 + 45, 15 = 45 − 30). Distracteurs : triplets qui ne
 * font pas 180 (30-45-90, 45-60-90), confusion des deux équerres, « 75° avec
 * une seule équerre ».
 * P3 : 30, 45, 60, 90 et leurs sommes / différences, entières.
 * P5 : dessin d'une équerre codée (petit carré ; traits d'égalité pour la 45-45-90).
 * P6 : le rapporteur est justement ce qu'on évite ; on ne mesure rien.
 * P8 : exigibles — triangle rectangle, isocèle, angle droit, accoler.
 * P10 : « vérifie que la somme vaut 180 » est un contrôle ; la nature isocèle se
 * justifie par les angles (deux angles de 45°).
 * P11 : croire qu'une équerre trace n'importe quel angle ; confondre les deux.
 * P12 : sans amont direct pour les mesures : la carte 1 sert de réactivation.
 * INVARIANTS :
 *   - l'équerre est DESSINÉE à ses vrais angles (côtés 1 et √3, ou 1 et 1),
 *     codée (petit carré, traits d'égalité pour la 45-45-90), tournée et
 *     éventuellement retournée ; les angles sont vérifiés par calcul (throw) ;
 *   - data-angles et data-cible dans le SVG pour les solveurs ; réponses
 *     ENTIÈRES ; pas de calculatrice ;
 *   - les deux équerres sont NOMMÉES par leurs angles (30-60-90, 45-45-90) ;
 *   - V/F : fenêtre stricte des 4 dernières lignes fixes servies.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';

const ATTENDU_ID = 1075;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_ANGLES = 'Les angles de mon équerre';
const L_TRACER = 'Quelle équerre pour cet angle ?';
const L_VF = 'Équerres : vrai ou faux ?';

const EQUERRES = {
    '30-60-90': { nom: 'l\'équerre 30-60-90', angles: [30, 60, 90], catA: Math.sqrt(3), catB: 1 },
    '45-45-90': { nom: 'l\'équerre 45-45-90', angles: [45, 45, 90], catA: 1, catB: 1 },
};
const ANGLES_EQUERRES = [30, 45, 60, 90];

// ─── Géométrie de l'équerre ────────────────────────────────────────────────
// Sommets : C (angle droit) à l'origine, A sur l'axe des x à distance catA,
// B sur l'axe des y à distance catB. Angle en A = atan(catB / catA).

const construireEquerre = (cle) => {
    const E = EQUERRES[cle];
    const pts = [[E.catA, 0], [0, E.catB], [0, 0]];   // A, B, C
    const angleA = Math.round((Math.atan2(E.catB, E.catA) * 180) / Math.PI);
    const angleB = 90 - angleA;
    const angles = { A: angleA, B: angleB, C: 90 };
    // Garde : les angles calculés sont ceux de l'équerre annoncée.
    const attendus = [...E.angles].sort((u, v) => u - v);
    if (JSON.stringify([angleA, angleB, 90].sort((u, v) => u - v)) !== JSON.stringify(attendus)) throw new Error(`gen_1075 : angles de ${cle} contredits`);
    const rot = randInt(0, 359); const miroir = Math.random() < 0.5;
    const rad = (rot * Math.PI) / 180;
    const tourne = pts.map(([x, y]) => { const mx = miroir ? -x : x; return [mx * Math.cos(rad) - y * Math.sin(rad), mx * Math.sin(rad) + y * Math.cos(rad)]; });
    return { cle, nom: E.nom, angles, pts: tourne, rot, miroir };
};

const W = 320; const H = 240; const MARGE = 34;
const f1 = (v) => v.toFixed(1);
const NOMS = ['A', 'B', 'C'];

const svgEquerre = (eq, options = {}) => {
    const { pts, angles } = eq;
    const xs = pts.map((p) => p[0]); const ys = pts.map((p) => p[1]);
    const xmin = Math.min(...xs); const xmax = Math.max(...xs); const ymin = Math.min(...ys); const ymax = Math.max(...ys);
    const ech = Math.min((W - 2 * MARGE) / (xmax - xmin), (H - 2 * MARGE) / (ymax - ymin));
    const X = (x) => MARGE + (x - xmin) * ech + ((W - 2 * MARGE) - (xmax - xmin) * ech) / 2;
    const Y = (y) => MARGE + (ymax - y) * ech + ((H - 2 * MARGE) - (ymax - ymin) * ech) / 2;
    const P = pts.map((p) => [X(p[0]), Y(p[1])]);
    const vec = (U, V) => [V[0] - U[0], V[1] - U[1]]; const norme = (u) => Math.hypot(u[0], u[1]);
    const unit = (u) => { const L = norme(u) || 1; return [u[0] / L, u[1] / L]; };
    let s = `<path d="M ${f1(P[0][0])} ${f1(P[0][1])} L ${f1(P[1][0])} ${f1(P[1][1])} L ${f1(P[2][0])} ${f1(P[2][1])} Z" fill="#fef3c7" stroke="#1E3A8A" stroke-width="2.4"/>`;
    // petit carré en C
    const uCA = unit(vec(P[2], P[0])); const uCB = unit(vec(P[2], P[1])); const k = 12;
    s += `<polyline points="${f1(P[2][0] + uCA[0] * k)},${f1(P[2][1] + uCA[1] * k)} ${f1(P[2][0] + (uCA[0] + uCB[0]) * k)},${f1(P[2][1] + (uCA[1] + uCB[1]) * k)} ${f1(P[2][0] + uCB[0] * k)},${f1(P[2][1] + uCB[1] * k)}" fill="none" stroke="#111827" stroke-width="1.8" data-code="carre"/>`;
    // traits d'égalité pour la 45-45-90 (CA = CB)
    if (eq.cle === '45-45-90') {
        [[P[2], P[0]], [P[2], P[1]]].forEach(([U, V], i) => {
            const m = [(U[0] + V[0]) / 2, (U[1] + V[1]) / 2]; const u = unit(vec(U, V)); const n = [-u[1], u[0]];
            s += `<line x1="${f1(m[0] - n[0] * 6)}" y1="${f1(m[1] - n[1] * 6)}" x2="${f1(m[0] + n[0] * 6)}" y2="${f1(m[1] + n[1] * 6)}" stroke="#111827" stroke-width="2" data-code="tick" data-cote="${i}"/>`;
        });
    }
    // arcs et étiquettes des angles aigus (valeurs ou ?)
    ['A', 'B'].forEach((nom, i) => {
        const S = P[i]; const U = P[(i + 1) % 3]; const V = P[(i + 2) % 3];
        const u1 = unit(vec(S, U)); const u2 = unit(vec(S, V));
        const theta = angles[nom];
        // arc échantillonné à l'intérieur de l'angle
        const a1 = Math.atan2(u1[1], u1[0]); let d = Math.atan2(u2[1], u2[0]) - a1;
        while (d > Math.PI) d -= 2 * Math.PI; while (d < -Math.PI) d += 2 * Math.PI;
        const r = 20; let arc = '';
        for (let t = 0; t <= 10; t++) { const ang = a1 + (d * t) / 10; arc += `${t === 0 ? 'M' : 'L'} ${f1(S[0] + r * Math.cos(ang))} ${f1(S[1] + r * Math.sin(ang))} `; }
        s += `<path d="${arc}" fill="none" stroke="#B45309" stroke-width="1.6"/>`;
        const txt = options.valeurs?.[nom];
        if (txt) {
            const bis = unit([u1[0] + u2[0], u1[1] + u2[1]]);
            const recul = Math.max(38, 12 / Math.tan((theta * Math.PI) / 360) + 8);
            s += `<text x="${f1(S[0] + bis[0] * recul)}" y="${f1(S[1] + bis[1] * recul + 4)}" font-size="12.5" font-weight="700" fill="#B45309" text-anchor="middle" data-valeur="${nom}">${txt}</text>`;
        }
    });
    if (options.valeurs?.C) {
        const bis = unit([uCA[0] + uCB[0], uCA[1] + uCB[1]]);
        s += `<text x="${f1(P[2][0] + bis[0] * 34)}" y="${f1(P[2][1] + bis[1] * 34 + 4)}" font-size="12.5" font-weight="700" fill="#B45309" text-anchor="middle" data-valeur="C">${options.valeurs.C}</text>`;
    }
    const dataAngles = NOMS.map((n) => `${n}:${angles[n]}`).join('|');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial" data-angles="${dataAngles}" data-cible="${options.cible || ''}" data-equerre="${eq.cle}">${s}</svg>`;
};

// ─── Carte 1a : l'angle marqué (saisie) ────────────────────────────────────

let derniereSignatureAngle1075 = null;

const genAngleSaisie = () => {
    let payload = construireAngleSaisie();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureAngle1075; t++) payload = construireAngleSaisie();
    derniereSignatureAngle1075 = payload._meta.params.signature;
    return payload;
};

const construireAngleSaisie = () => {
    const eq = construireEquerre(randomChoice(['30-60-90', '45-45-90']));
    const cible = randomChoice(['A', 'B', 'C', 'A', 'B']);   // l'angle droit une fois sur cinq
    const reponse = eq.angles[cible];
    const svg = svgEquerre(eq, { valeurs: { [cible]: '?' }, cible });
    const texte = '<p>Voici l\'une de tes deux équerres, dessinée avec ses vrais angles.</p><p>Quelle est la mesure de l\'angle marqué « ? » ?</p>';
    const payload = makeSaisie(String(reponse), texte, {
        ...opts(L_ANGLES), suffixe: '°', elements_additionnels: imgElement(svg, 'medium'),
    });
    payload.contenu = { ...payload.contenu, sousType: eq.cle };
    payload._meta.params = { equerre: eq.cle, cible, reponse, signature: `${eq.cle}-${cible}-${eq.rot}` };
    return payload;
};

// ─── Carte 1b : le triplet (QCM) ───────────────────────────────────────────

// Leurres (revue 2026-09-04, A3) : l'autre équerre, un triplet POSSIBLE mais étranger
// aux équerres (40-50-90, il faut connaître les vraies mesures), et un triplet
// impossible (30-45-90, le piège de la somme).
const TRIPLETS = ['30°, 60° et 90°', '45°, 45° et 90°', '40°, 50° et 90°', '30°, 45° et 90°'];
const TRIPLET_DE = { '30-60-90': TRIPLETS[0], '45-45-90': TRIPLETS[1] };

let derniereSignatureTriplet1075 = null;

const genTripletQcm = () => {
    let payload = construireTripletQcm();
    for (let t = 0; t < 8 && payload._meta.params.signature === derniereSignatureTriplet1075; t++) payload = construireTripletQcm();
    derniereSignatureTriplet1075 = payload._meta.params.signature;
    return payload;
};

const construireTripletQcm = () => {
    const eq = construireEquerre(randomChoice(['30-60-90', '45-45-90']));
    const bonne = TRIPLET_DE[eq.cle];
    const svg = svgEquerre(eq, {});
    const texte = '<p>Voici l\'une de tes deux équerres, dessinée avec ses vrais angles.</p><p>Quelles sont les mesures de ses trois angles ?</p>';
    const payload = makeQcm([...TRIPLETS], bonne, texte, {
        ...opts(L_ANGLES), elements_additionnels: imgElement(svg, 'medium'),
    });
    payload._meta.params = { equerre: eq.cle, signature: `${eq.cle}-${eq.rot}-${eq.miroir}` };
    return payload;
};

// ─── Carte 2a : quelle équerre ? (QCM) ─────────────────────────────────────

const OPTIONS_EQUERRE = ['l\'équerre 30-60-90', 'l\'équerre 45-45-90', 'les deux équerres', 'aucune des deux, pas directement'];
const EQUERRE_POUR = (n) => {
    if (n === 90) return OPTIONS_EQUERRE[2];
    if (n === 30 || n === 60) return OPTIONS_EQUERRE[0];
    if (n === 45) return OPTIONS_EQUERRE[1];
    return OPTIONS_EQUERRE[3];
};
const ANGLES_DEMANDES = [30, 45, 60, 90, 75, 105, 120, 135, 15];

let derniersAnglesTracer1075 = [];

const genTracerQcm = () => {
    // Deux tirages sur trois : un angle DIRECT (30, 45, 60, 90) ; un sur trois : un
    // angle composé (revue 2026-09-04, A2 : « aucune » dominait à 55 %).
    const direct = Math.random() < 2 / 3;
    const groupe = ANGLES_DEMANDES.filter((x) => ANGLES_EQUERRES.includes(x) === direct);
    const dispo = groupe.filter((n) => !derniersAnglesTracer1075.includes(n));
    const n = randomChoice(dispo.length ? dispo : groupe);
    derniersAnglesTracer1075 = [...derniersAnglesTracer1075, n].slice(-3);
    const bonne = EQUERRE_POUR(n);
    const texte = `<p>Sans rapporteur, avec une seule équerre posée une seule fois, quelle équerre permet de tracer directement un angle de ${n}° ?</p>`;
    const payload = makeQcm([...OPTIONS_EQUERRE], bonne, texte, opts(L_TRACER));
    payload.contenu = { ...payload.contenu, sousType: bonne === OPTIONS_EQUERRE[3] ? 'compose' : 'direct' };
    payload._meta.params = { n, bonne };
    return payload;
};

// La saisie « accoler / retrancher » (questions c et d de la fiche) a été retirée :
// combiner les angles n'est pas les connaître (décision prof 2026-09-04).

// ─── Carte 3 : vrai ou faux ? ──────────────────────────────────────────────

const POOL_VF = [
    { l: 'L\'équerre 45-45-90 est un triangle rectangle isocèle.', v: true },
    { l: 'Les deux équerres ont un angle droit.', v: true },
    { l: 'Dans chaque équerre, la somme des trois angles vaut 180°.', v: true },
    { l: 'L\'équerre 30-60-90 a trois angles de mesures différentes.', v: true },
    { l: 'L\'angle droit est le seul angle commun aux deux équerres.', v: true },
    { l: 'Les deux angles aigus de l\'équerre 45-45-90 ont la même mesure.', v: true },
    { l: 'L\'équerre 30-60-90 est un triangle isocèle.', v: false },
    { l: 'L\'équerre 30-60-90 a un angle de 90° et un angle de 45°.', v: false },
    { l: 'Les deux équerres sont des triangles rectangles isocèles.', v: false },
    { l: 'Les deux équerres ont les mêmes angles.', v: false },
    { l: 'L\'équerre 45-45-90 a trois angles de mesures différentes.', v: false },
    { l: 'Le plus petit angle de l\'équerre 30-60-90 mesure 60°.', v: false },
];
if (POOL_VF.filter((it) => it.v).length !== 6) throw new Error('gen_1075 : pool V/F déséquilibré');

let derniersServisVf1075 = [];

const genVf = () => {
    for (let essai = 0; essai < 60; essai++) {
        // Deux lignes NUMÉRIQUES, vérité tirée à pile ou face puis valeurs (sur les mesures, pas sur l'accolement).
        const vraie1 = Math.random() < 0.5;
        const eq1 = randomChoice(['30-60-90', '45-45-90']);
        const n1 = vraie1 ? randomChoice(EQUERRES[eq1].angles) : randomChoice([15, 30, 45, 60, 75, 120].filter((x) => !EQUERRES[eq1].angles.includes(x)));
        const vraie2 = Math.random() < 0.5;
        const eqCle = randomChoice(['30-60-90', '45-45-90']);
        const directs = EQUERRES[eqCle].angles;
        // jamais le même couple (équerre, angle) que la première ligne : ce serait la même affirmation deux fois
        const candidatsN = (vraie2 ? directs : ANGLES_EQUERRES.concat([75, 120]).filter((x) => !directs.includes(x))).filter((x) => !(eqCle === eq1 && x === n1));
        if (candidatsN.length === 0) continue;
        const n = randomChoice(candidatsN);
        const nomMaj = (cle) => EQUERRES[cle].nom.charAt(0).toUpperCase() + EQUERRES[cle].nom.slice(1);
        const numeriques = [
            { l: `${nomMaj(eq1)} a un angle de ${n1}°.`, v: EQUERRES[eq1].angles.includes(n1) },
            { l: `${nomMaj(eqCle)} permet de tracer directement un angle de ${n}°.`, v: directs.includes(n) },
        ];
        if (numeriques[0].v !== vraie1 || numeriques[1].v !== vraie2) throw new Error('gen_1075 : ligne numérique mal calibrée');
        // A8 : lignes fixes déductibles des numériques exclues.
        const exclues = [];
        const parle = (eq, x) => (eq1 === eq && n1 === x) || (eqCle === eq && n === x);
        if (parle('30-60-90', 45)) exclues.push('L\'équerre 30-60-90 a un angle de 90° et un angle de 45°.');
        if (parle('30-60-90', 60) || parle('30-60-90', 30)) exclues.push('Le plus petit angle de l\'équerre 30-60-90 mesure 60°.');
        const dispo = POOL_VF.filter((it) => !derniersServisVf1075.includes(it.l) && !exclues.includes(it.l));
        const fixes = shuffle(dispo).slice(0, 2);
        const tirage = shuffle([...numeriques, ...fixes]);
        const nbFaux = tirage.filter((it) => !it.v).length;
        if (nbFaux < 1 || nbFaux > 3) continue;
        derniersServisVf1075 = [...derniersServisVf1075, ...fixes.map((it) => it.l)].slice(-4);
        const payload = makeVraiFaux(
            tirage.map((it) => it.l),
            tirage.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            opts(L_VF),
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        return payload;
    }
    throw new Error('gen_1075 : V/F sans mixité après 60 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    angles_saisie: genAngleSaisie,
    angles_triplet_qcm_unique: genTripletQcm,
    tracer_qcm_unique: genTracerQcm,
    vf_tableau_vf: genVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', triplet_qcm_unique: 'QCM', qcm_unique: 'QCM', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'angles', label: L_ANGLES, formatsList: buildFormats('angles') },
    { id: 'tracer', label: L_TRACER, formatsList: buildFormats('tracer') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
